package br.com.heyagenda.ui.components

import androidx.compose.foundation.border
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import br.com.heyagenda.ui.theme.AuraEmerald
import br.com.heyagenda.ui.theme.AuraOnEmerald
import br.com.heyagenda.ui.theme.AuraPrimaryButtonBg
import br.com.heyagenda.ui.theme.AuraPrimaryButtonText
import br.com.heyagenda.ui.theme.AuraSecondaryButtonBg
import br.com.heyagenda.ui.theme.AuraSecondaryButtonBorder
import br.com.heyagenda.ui.theme.AuraTextPrimary
import br.com.heyagenda.ui.theme.AuraTheme

enum class AuraButtonVariant {
    Primary,    // Solid Pill (White in Dark, Dark Slate in Light)
    Secondary,  // Translucent Glass Pill with subtle border
    Emerald,    // Vibrant Mint/Emerald Pill (PIX / Action)
    Text        // Minimalist borderless pill
}

enum class AuraButtonSize {
    Small,   // 36.dp height
    Medium,  // 44.dp height
    Large    // 52.dp height
}

@Composable
fun AuraButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    variant: AuraButtonVariant = AuraButtonVariant.Primary,
    size: AuraButtonSize = AuraButtonSize.Medium,
    enabled: Boolean = true,
    isLoading: Boolean = false,
    containerColor: Color? = null,
    contentColor: Color? = null,
    leadingIcon: (@Composable RowScope.() -> Unit)? = null,
    trailingIcon: (@Composable RowScope.() -> Unit)? = null
) {
    val height = when (size) {
        AuraButtonSize.Small -> 36.dp
        AuraButtonSize.Medium -> 44.dp
        AuraButtonSize.Large -> 52.dp
    }

    val fontSize = when (size) {
        AuraButtonSize.Small -> 12.sp
        AuraButtonSize.Medium -> 13.sp
        AuraButtonSize.Large -> 15.sp
    }

    val horizontalPadding = when (size) {
        AuraButtonSize.Small -> 14.dp
        AuraButtonSize.Medium -> 20.dp
        AuraButtonSize.Large -> 24.dp
    }

    val actualContainerColor = containerColor ?: when (variant) {
        AuraButtonVariant.Primary -> AuraPrimaryButtonBg
        AuraButtonVariant.Secondary -> AuraSecondaryButtonBg
        AuraButtonVariant.Emerald -> AuraEmerald
        AuraButtonVariant.Text -> Color.Transparent
    }

    val actualContentColor = contentColor ?: when (variant) {
        AuraButtonVariant.Primary -> AuraPrimaryButtonText
        AuraButtonVariant.Secondary -> AuraTextPrimary
        AuraButtonVariant.Emerald -> AuraOnEmerald
        AuraButtonVariant.Text -> AuraTextPrimary
    }

    val effectiveContentColor = if (enabled && !isLoading) actualContentColor else actualContentColor.copy(alpha = 0.5f)

    val borderStroke = if (variant == AuraButtonVariant.Secondary) {
        androidx.compose.foundation.BorderStroke(width = 1.dp, color = AuraSecondaryButtonBorder)
    } else null

    Button(
        onClick = onClick,
        modifier = modifier.height(height),
        enabled = enabled && !isLoading,
        shape = CircleShape,
        border = borderStroke,
        colors = ButtonDefaults.buttonColors(
            containerColor = actualContainerColor,
            contentColor = actualContentColor,
            disabledContainerColor = actualContainerColor.copy(alpha = 0.4f),
            disabledContentColor = actualContentColor.copy(alpha = 0.4f)
        ),
        elevation = null,
        contentPadding = PaddingValues(horizontal = horizontalPadding, vertical = 0.dp)
    ) {
        CompositionLocalProvider(LocalContentColor provides effectiveContentColor) {
            if (isLoading) {
                CircularProgressIndicator(
                    modifier = Modifier.size(16.dp),
                    color = actualContentColor,
                    strokeWidth = 2.dp
                )
                Spacer(modifier = Modifier.width(8.dp))
            } else if (leadingIcon != null) {
                leadingIcon()
                Spacer(modifier = Modifier.width(6.dp))
            }

            Text(
                text = text,
                fontWeight = if (variant == AuraButtonVariant.Primary || variant == AuraButtonVariant.Emerald) FontWeight.Bold else FontWeight.SemiBold,
                fontSize = fontSize,
                color = effectiveContentColor
            )

            if (!isLoading && trailingIcon != null) {
                Spacer(modifier = Modifier.width(6.dp))
                trailingIcon()
            }
        }
    }
}
